import {
  Box,
  Flex,
  Spinner,
  Text,
  Tooltip,
  Table,
  Tr,
  Thead,
  Th,
  Tbody,
  Td
} from '@chakra-ui/react';
import {
  faEdit,
  faFile,
  faPlus,
  faTrash,
  faWarning
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  ButtonIcon,
  ButtonText,
  Modal,
  OutlineCard,
  useToast
} from '@morressier/alembic';
import { useQuery } from '@tanstack/react-query';
import {
  fetchSubmissionContext,
  fetchSubmissionContextArticleTypes
} from 'src/api/queries';
import {
  ISubmissionContext,
  journalPeerReviewModels
} from '@publishing-engine/domain/models';
import { useNavigate } from 'react-router-dom';
import { useMemo, useState } from 'react';
import { useFlag } from '@unleash/proxy-client-react';
import { FEATURE_FLAGS } from '../../../../../../../constants';
import AddArticleTypeModal from './AddArticleTypeModal';
import { useDeleteArticleType } from 'src/api/mutations';
import ArticleTypeConfigModal from '../../../ArticleTypes/ArticleTypeConfigModal';
import GeneralInfoEditableSection from './GeneralInfo/GeneralInfoEditableSection';
import {
  buildPeerReviewModelFieldConfig,
  getConfiguredPeerReviewModel
} from './GeneralInfo/peerReviewModelConfig';

interface Props {
  orgShortname: string;
  journalId: string;
}

/**
 * Chakra's tooltip opens on focus as well as on hover, and it opens with a
 * timeout. On touch devices a tap focuses the trigger, the click handler runs
 * while the tooltip is still closed (`closeOnClick` only closes an already open
 * tooltip), and the scheduled open then fires over the modal the click opened.
 * Driving `isOpen` from pointer events, ignoring touch and closing on pointer
 * down keeps the tooltip to deliberate hover. Same approach as `RinggoldBadge`.
 */
const HoverTooltip: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children
}) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Tooltip hasArrow isOpen={isOpen} label={label} placement="top">
      <Box
        display="inline-flex"
        onPointerEnter={event => {
          if (event.pointerType !== 'touch') setIsOpen(true);
        }}
        onPointerLeave={() => setIsOpen(false)}
        onPointerDown={() => setIsOpen(false)}
      >
        {children}
      </Box>
    </Tooltip>
  );
};

export const ArticleTypesSection: React.FC<Props> = ({
  orgShortname,
  journalId
}) => {
  const [showConfigModal, setShowConfigModal] = useState({
    isOpen: false,
    articleType: { id: '', name: '', description: '' }
  });

  const articleTypeFormEnabled = useFlag(FEATURE_FLAGS.articleTypeCreationForm);
  const navigate = useNavigate();
  const navigateToCreateArticleType = () => {
    return navigate(
      `/app/workspace/${orgShortname}/manage/journals/${journalId}/article-types/create`
    );
  };

  const { data: journalArticleTypes = { articleTypes: [] }, isLoading } =
    useQuery({
      queryKey: ['journalArticleTypes', journalId],
      queryFn: () => fetchSubmissionContextArticleTypes(journalId),
      enabled: !!journalId
    });

  // Served from the Configure page's cache — same query key, no extra request.
  const { data: journal } = useQuery({
    queryKey: ['submission-context', journalId],
    queryFn: () => (journalId ? fetchSubmissionContext(journalId) : null),
    enabled: !!journalId
  });

  /**
   * The table rows are keyed by article type category, but the peer review model
   * is stored per article workflow, so the row needs the workflow behind its
   * category to read and write its own entry.
   *
   * The journal's own record is read first because the endpoint's `articleTypes`
   * list leaves out the base article type, which the table still shows a row for —
   * without the workflow behind it, that row cannot be given a model at all.
   */
  const articleWorkflowIdByArticleTypeId = useMemo(() => {
    const map = new Map<string, string>();
    if (!journal) return map;

    Object.entries(journal.articleWorkflows ?? {}).forEach(
      ([articleTypeId, articleWorkflowId]) =>
        map.set(articleTypeId, articleWorkflowId)
    );

    journal.articleTypes?.forEach(articleType => {
      if (!map.has(articleType.articleTypeCategoryId)) {
        map.set(
          articleType.articleTypeCategoryId,
          articleType.articleWorkflowId
        );
      }
    });

    return map;
  }, [journal]);

  /**
   * Only an explicitly configured model is listed, so an article type with none
   * reads as "No data available" rather than as the model it would fall back to —
   * otherwise a journal could be blocked from activating while every row appeared
   * to have a model.
   */
  const peerReviewModelByArticleTypeId = useMemo(() => {
    const map = new Map<string, string>();
    if (!journal) return map;

    articleWorkflowIdByArticleTypeId.forEach(
      (articleWorkflowId, articleTypeId) => {
        const model = getConfiguredPeerReviewModel(journal, articleWorkflowId);

        if (model) map.set(articleTypeId, journalPeerReviewModels[model]);
      }
    );

    return map;
  }, [journal, articleWorkflowIdByArticleTypeId]);

  const onCloseConfigModal = () => {
    setShowConfigModal({
      isOpen: false,
      articleType: { id: '', name: '', description: '' }
    });
  };

  return (
    <Flex flexDirection="column">
      {showConfigModal.isOpen ? (
        <ArticleTypeConfigModal
          articleType={showConfigModal.articleType}
          onClose={onCloseConfigModal}
          activeJournalId={journalId}
        />
      ) : null}

      <Flex
        justifyContent="space-between"
        alignItems={{ base: 'flex-start', md: 'center' }}
        flexWrap="wrap"
        gap={2}
        marginBottom={4}
      >
        <Text textStyle="headingSection">Article Types</Text>
        {articleTypeFormEnabled && (
          <ButtonText onClick={navigateToCreateArticleType}>
            Add Article Type
          </ButtonText>
        )}

        <AddArticleTypeModal />
      </Flex>
      {isLoading ? (
        <Spinner />
      ) : journalArticleTypes.articleTypes.length ? (
        <Box width="100%" mt={4} overflowX="auto">
          <Table minWidth="320px" mx="auto">
            <Thead>
              <Tr
                data-testId="article-table-header"
                gap={4}
                sx={{
                  '& th': {
                    textTransform: 'none',
                    color: 'black',
                    background: 'white'
                  }
                }}
              >
                <Th p={{ base: 2, md: 4 }}>
                  <Text textStyle="headingItem" whiteSpace="nowrap">
                    Name
                  </Text>
                </Th>
                <Th p={{ base: 2, md: 4 }}>
                  <Text textStyle="headingItem" whiteSpace="nowrap">
                    Peer Review Model
                  </Text>
                </Th>
                <Th></Th>
              </Tr>
            </Thead>
            <Tbody>
              {journalArticleTypes.articleTypes.map(articleType => (
                <ArticleTypeRow
                  key={articleType.id}
                  {...articleType}
                  journalId={journalId}
                  journal={journal}
                  articleWorkflowId={articleWorkflowIdByArticleTypeId.get(
                    articleType.id
                  )}
                  peerReviewModel={peerReviewModelByArticleTypeId.get(
                    articleType.id
                  )}
                  onOpenConfigModal={() =>
                    setShowConfigModal({
                      isOpen: true,
                      articleType: {
                        id: articleType.id,
                        name: articleType.name,
                        description: ''
                      }
                    })
                  }
                />
              ))}
            </Tbody>
          </Table>
        </Box>
      ) : (
        <OutlineCard padding={8} backgroundColor="none">
          <Flex gap={8} flexDirection="column" alignItems="flex-start">
            <Flex
              width="100%"
              backgroundColor="neutralSecondaryFill"
              height="112px"
              borderRadius="base"
              justifyContent="center"
              alignItems="center"
            >
              <FontAwesomeIcon icon={faFile} />
            </Flex>
            <Box>
              <Text>No Article Types have been added to this Journal</Text>
              <Text>
                Article Types can be reused across all your publications,
                streamlining your workflow and ensuring consistency.
              </Text>
            </Box>
            {articleTypeFormEnabled && (
              <Button
                variant="secondary"
                iconLeft={<FontAwesomeIcon icon={faPlus} />}
                onClick={navigateToCreateArticleType}
              >
                Add Article Type
              </Button>
            )}
          </Flex>
        </OutlineCard>
      )}
    </Flex>
  );
};

const ArticleTypeRow: React.FC<{
  name: string;
  id: string;
  documentSchemaId: string;
  journalId: string;
  journal?: ISubmissionContext | null;
  articleWorkflowId?: string;
  peerReviewModel?: string;
  onOpenConfigModal: () => void;
}> = ({
  id,
  name,
  journalId,
  journal,
  articleWorkflowId,
  peerReviewModel,
  onOpenConfigModal
}) => {
  const articleTypeDebugViewEnabled = useFlag(
    FEATURE_FLAGS.articleTypeDebugView
  );
  const peerReviewModelFieldConfig = useMemo(
    () =>
      articleWorkflowId
        ? buildPeerReviewModelFieldConfig(articleWorkflowId)
        : undefined,
    [articleWorkflowId]
  );

  /** An article type with no model of its own reads as the muted empty state. */
  const peerReviewModelValue = peerReviewModel ? (
    <Text>{peerReviewModel}</Text>
  ) : (
    <Text textStyle="textBody" color="GrayText">
      No data available
    </Text>
  );

  return (
    <>
      <Tr
        data-testId="aritcle-table-row"
        gap={4}
        background="white"
        alignItems="center"
        _last={{ borderBottom: 'none' }}
        key={id}
      >
        <Td isTruncated>
          <Text isTruncated>{name}</Text>
        </Td>
        <Td>
          {/* Without a workflow, or before the journal query resolves, there is
              nothing to read the model from or write it back to. The model stays
              editable while the journal is live, so an article type without one
              can always be given a model. */}
          {journal && peerReviewModelFieldConfig ? (
            <GeneralInfoEditableSection
              label="Peer Review Model"
              value={peerReviewModelValue}
              journal={journal}
              fieldConfig={peerReviewModelFieldConfig}
              layout="inline"
            />
          ) : (
            peerReviewModelValue
          )}
        </Td>
        <Td>
          {articleTypeDebugViewEnabled && (
            <Flex justifyContent="flex-end" width="100%" gap={2}>
              <HoverTooltip label="Edit configuration">
                <ButtonIcon
                  variant="secondary"
                  ariaLabel="Edit aritcle"
                  icon={<FontAwesomeIcon icon={faEdit} />}
                  onClick={onOpenConfigModal}
                />
              </HoverTooltip>

              <DeleteArticleTypeRow journalId={journalId} articleTypeId={id} />
            </Flex>
          )}
        </Td>
      </Tr>
    </>
  );
};

const DeleteArticleTypeRow = ({
  journalId,
  articleTypeId
}: {
  journalId: string;
  articleTypeId: string;
}) => {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const deleteMutation = useDeleteArticleType(journalId);

  const toast = useToast();

  const handleDelete = async () => {
    try {
      const response = await deleteMutation.mutateAsync(articleTypeId);

      if (!response?.isSuccess) {
        toast({
          variant: 'error',
          title: response.message
        });
        return;
      }

      toast({
        variant: 'success',
        title: 'Article type deleted successfully'
      });

      setIsDeleteModalOpen(false);
    } catch (error: any) {
      toast({
        variant: 'error',
        title: error?.response?.data?.message || 'Failed to delete article type'
      });
    }
  };

  const onClose = () => setIsDeleteModalOpen(false);

  return (
    <Flex>
      <HoverTooltip label="Delete">
        <ButtonIcon
          variant="caution"
          ariaLabel="delete aritcle"
          icon={<FontAwesomeIcon icon={faTrash} style={{ color: 'red' }} />}
          onClick={() => setIsDeleteModalOpen(true)}
        />
      </HoverTooltip>

      <Modal
        isOpen={isDeleteModalOpen}
        onClose={onClose}
        width={{ base: 'calc(100vw - 16px)', md: '448px' } as any}
      >
        <Flex padding={{ base: 4, md: 8 }} gap={6} flexDirection="column">
          <Flex flexDirection="column" gap={2}>
            <Text
              textStyle="headingGroup"
              color="actionSecondaryCautionContent"
            >
              <FontAwesomeIcon icon={faWarning} /> Delete Article Type?
            </Text>
            <Text textStyle="textBody">
              Are you sure you want to delete this article type?
            </Text>
          </Flex>

          <Flex gap={2} justifyContent="flex-end">
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="caution"
              onClick={handleDelete}
              isLoading={deleteMutation.isPending}
            >
              Delete
            </Button>
          </Flex>
        </Flex>
      </Modal>
    </Flex>
  );
};
